<template>
  <div id="app">
    <div class="container">
      <!-- Lista de odontogramas do paciente -->
      <b-card v-if="!showOdontogramaForm && !loading">
        <b-card-body>
          <div class="d-flex justify-content-end mb-3">
            <b-button
              variant="primary"
              @click="createOdontogram"
            >
              Criar Odontograma
            </b-button>
          </div>
          <div
            v-if="odontograms.length === 0"
            class="text-center py-4"
          >
            <feather-icon
              icon="FileTextIcon"
              size="48"
              class="text-muted mb-2"
            />
            <p class="text-muted">
              Nenhum odontograma encontrado para este paciente.
            </p>
          </div>

          <b-list-group v-else>
            <b-list-group-item
              v-for="odontogram in odontograms"
              :key="odontogram.id"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <h6 class="mb-1">
                  Odontograma #{{ odontogram.id }}
                </h6>
                <small class="d-block text-muted">
                  Criado em: {{ formatHyphenToSlash(odontogram.data) }}
                </small>
                <small
                  v-if="odontogram.profissional"
                  class="d-block text-primary"
                >
                  Responsável Atual:
                  {{
                    odontogram.profissional.nome ||
                      (odontogram.profissional.registro &&
                        odontogram.profissional.registro.nome) ||
                      "Não informado"
                  }}
                </small>
                <small
                  v-if="
                    odontogram.profissionalIniciou &&
                      odontogram.profissionalIniciou.id !==
                      (odontogram.profissional && odontogram.profissional.id)
                  "
                  class="d-block text-secondary"
                >
                  Iniciado por:
                  {{
                    odontogram.profissionalIniciou.nome ||
                      (odontogram.profissionalIniciou.registro &&
                        odontogram.profissionalIniciou.registro.nome) ||
                      "Não informado"
                  }}
                </small>
              </div>
              <div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1"
                  title="Editar Odontograma"
                  @click="editOdontogram(odontogram)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card-body>
      </b-card>

      <!-- Loading spinner -->
      <div
        v-if="loading"
        class="text-center py-4"
      >
        <b-spinner
          variant="primary"
          label="Carregando..."
        />
        <p class="mt-2 text-muted">
          Carregando odontogramas...
        </p>
      </div>

      <!-- Formulário de odontograma (será mostrado quando showOdontogramaForm = true) -->
      <div v-if="showOdontogramaForm">
        <b-button
          variant="outline-secondary"
          size="sm"
          class="mb-3"
          @click="backToList"
        >
          <feather-icon icon="ArrowLeftIcon" />
          Voltar para Lista
        </b-button>
        <DigitalOdontogram
          :odontograma="odontograma"
          @tooth-updated="updateTooth"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BButton,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import odontogramaServices from '@/api/odontograma/odontogramaServices'
import { formatHyphenToSlash } from '@/utils/dateFormatter'
import DigitalOdontogram from './components/DigitalOdontogram.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BButton,
    BSpinner,
    BBadge,
    DigitalOdontogram,
  },
  computed: {
    ...mapState('atendimentoProfissionalModule', {
      atendimento: state => state.atendimento,
    }),
    patientId() {
      return this.atendimento?.registro?.id
    },
  },

  data() {
    return {
      showOdontogramaForm: false,
      loading: false,
      odontograms: [],
      currentDate: this.formatDateToDDMMYYYY(new Date()),
      odontograma: {},
    }
  },
  mounted() {
    if (this.patientId) {
      this.loadPatientOdontograms()
    }
  },

  methods: {
    formatHyphenToSlash(dataString) {
      return formatHyphenToSlash(dataString)
    },
    /**
     * Formata a data para o formato dd-mm-yyyy
     */
    formatDateToDDMMYYYY(date) {
      if (!date) return ''
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    },

    /**
     * Carrega os odontogramas do paciente usando o serviço getPatientById
     */
    async loadPatientOdontograms() {
      if (!this.patientId) {
        console.error('ID do paciente não encontrado')
        return
      }

      this.loading = true
      try {
        // Usar o serviço diretamente ou através do Vuex
        const response = await odontogramaServices.getPatientById(
          this.patientId,
        )
        this.odontograms = response.data.content || response.data || []
      } catch (error) {
        console.error('Erro ao carregar odontogramas do paciente:', error)
        this.$bvToast.toast('Erro ao carregar odontogramas do paciente', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },
    /**
     * Formata a data para exibição
     */
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    /**
     * Edita um odontograma existente
     */
    editOdontogram(odontogram) {
      this.$store
        .dispatch('odontogramaModule/getById', odontogram.id)
        .then(response => {
          this.odontograma = response
          this.showOdontogramaForm = true
        })
        .catch(error => {
          console.error('Erro ao carregar odontograma:', error)
        })
    },

    /**
     * Método legado mantido para compatibilidade
     */
    loadOdontograma() {
      this.showOdontogramaForm = true
    },
    createOdontogram() {
      this.$store
        .dispatch('odontogramaModule/generate', {
          paciente: this.atendimento.registro.id,
          profissional: this.atendimento.profissional.id,
          data: this.currentDate,
          ativo: true,
        })
        .then(response => {
          // Atualizar o objeto odontograma com os dados criados
          this.odontograma = response.data
          this.showOdontogramaForm = true
        })
        .catch(error => {
          console.error('Erro ao criar odontograma:', error)
          this.$bvToast.toast('Erro ao criar odontograma', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
        })
    },

    /**
     * Volta para a lista de odontogramas e atualiza a lista
     */
    async backToList() {
      this.showOdontogramaForm = false
      // Atualizar a lista de odontogramas para incluir o novo
      await this.loadPatientOdontograms()
    },

    /**
     * Atualiza um dente quando o componente DigitalOdontogram emite o evento
     */
    updateTooth(updatedTooth) {
      const index = this.odontograma.teeth.findIndex(
        tooth => tooth.id === updatedTooth.id,
      )
      if (index !== -1) {
        this.odontograma.teeth.splice(index, 1, updatedTooth)
        console.log('Dente atualizado:', updatedTooth)
      }
    },
  },
}
</script>
